import React, { useEffect, useMemo, useState } from 'react';
import { ApiScreeningClearanceRuleTestResult } from '../../../api';
import { SalvDropdown, SalvTable, SalvTablePaginationType, SalvTextField, Size } from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { createColumnHelper, PaginationState, SortingState } from '@tanstack/react-table';

const RULE_RESULTS_COLUMN = 'ruleResults';

const RESULT_OPTIONS: DropdownOption[] = [
  { key: 'true', label: 'True', value: 'true' },
  { key: 'false', label: 'False', value: 'false' },
  { key: 'null', label: 'Null', value: 'null' },
];

const PAGE_SIZE_OPTIONS: DropdownOption[] = [
  { key: '20', label: '20', value: '20' },
  { key: '50', label: '50', value: '50' },
  { key: '100', label: '100', value: '100' },
];

interface Props {
  results: ApiScreeningClearanceRuleTestResult[];
}

const attemptJSONPrettyPrint = (input: string) => {
  try {
    return JSON.stringify(JSON.parse(input), null, 2);
  } catch (_) {
    return input;
  }
};

const columnHelper = createColumnHelper<ApiScreeningClearanceRuleTestResult>();

const columns = [
  columnHelper.accessor('recordId', {
    header: 'Record ID',
    cell: ({ getValue }) => <pre>{getValue()}</pre>,
  }),
  columnHelper.accessor('ruleResults', {
    id: RULE_RESULTS_COLUMN,
    header: 'expression result',
    cell: ({ getValue }) => <pre>{attemptJSONPrettyPrint(getValue())}</pre>,
    enableSorting: true,
  }),
  columnHelper.accessor('viewedObject', {
    header: 'data',
    cell: ({ getValue }) => <pre>{attemptJSONPrettyPrint(getValue())}</pre>,
  }),
];

export const ClearanceRuleTestResults = ({ results }: Props) => {
  const [filter, setFilter] = useState<DropdownOption>();
  const [recordFilter, setRecordFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: RULE_RESULTS_COLUMN, desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Number(PAGE_SIZE_OPTIONS[0].value),
  });

  const filteredResults = useMemo(
    () =>
      results
        .filter((result) => !filter?.value || result.ruleResults === filter.value)
        .filter((result) => !recordFilter || result.recordId == recordFilter),
    [results, filter, recordFilter]
  );

  const sortedResults = useMemo(() => {
    const sort = sorting[0];
    if (!sort) {
      return filteredResults;
    }
    return filteredResults.toSorted((a, b) => {
      const cmp = a.ruleResults.localeCompare(b.ruleResults);
      return sort.desc ? -cmp : cmp;
    });
  }, [filteredResults, sorting]);

  const pageRows = useMemo(() => {
    const offset = pagination.pageIndex * pagination.pageSize;
    return sortedResults.slice(offset, offset + pagination.pageSize);
  }, [sortedResults, pagination]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filter, recordFilter, results]);

  return (
    <SalvTable
      title='Matches'
      className={'m-t-8'}
      data={pageRows}
      columns={columns}
      striped
      sorting={{ state: sorting, setSorting }}
      pagination={{
        type: SalvTablePaginationType.PAGINATION,
        total: filteredResults.length,
        state: pagination,
        setPagination,
      }}
      emptyMessage={'No results found'}
      actions={
        results.length ? (
          <>
            <SalvDropdown
              label='Expression result'
              options={RESULT_OPTIONS}
              value={filter}
              size={Size.SMALL}
              onChange={(value) => setFilter(value[0])}
            />
            <SalvTextField
              label='Record id'
              value={recordFilter}
              size={Size.SMALL}
              onChange={(value) => setRecordFilter(value)}
            />
            <SalvDropdown
              label='Page size'
              options={PAGE_SIZE_OPTIONS}
              value={PAGE_SIZE_OPTIONS.find((option) => Number(option.value) === pagination.pageSize)}
              clearable={false}
              size={Size.SMALL}
              onChange={(value) =>
                setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: Number(value[0].value) }))
              }
            />
          </>
        ) : undefined
      }
    />
  );
};

export default ClearanceRuleTestResults;
